import React, { useState, useEffect } from 'react';
import dbService from '../../../services/dbService';
import UserModal from './UserModal';
import { toast } from 'react-toastify';
import { PencilSquare, Trash, PersonPlusFill } from 'react-bootstrap-icons';
import { Button, Tooltip, OverlayTrigger } from 'react-bootstrap';
import { usePermissions } from '../../../hooks/usePermissions';
import { useAuth } from '../../../context/AuthContext';
import PageContainer from '../../layout/PageContainer';

// Helper component for disabled buttons with tooltips
const DisabledButton = ({ children, tooltipText, className = '' }) => {
    return (
        <OverlayTrigger overlay={<Tooltip>{tooltipText}</Tooltip>}>
            <span className={`d-inline-block ${className}`}>
                <Button variant="outline-secondary" size="sm" disabled style={{ pointerEvents: 'none' }}>
                    {children}
                </Button>
            </span>
        </OverlayTrigger>
    );
};

const UserList = () => {
  const [users, setUsers] = useState([]);
  const [modalShow, setModalShow] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const { permissions } = usePermissions(); // CORRECT: De-structure
  const { user: loggedInUser } = useAuth();

  const fetchUsers = async () => setUsers(await dbService.getAll('users'));
  useEffect(() => { fetchUsers(); }, []);

const handleSave = async (userToSave) => {
    try {
      if (userToSave.id) {
        await dbService.update('users', userToSave);
        await dbService.addLog({ user: loggedInUser.name, action: 'Updated User', details: `User: ${userToSave.name}` });
        toast.info('User updated successfully!');
      } else {
        await dbService.add('users', userToSave);
        await dbService.addLog({ user: loggedInUser.name, action: 'Created User', details: `User: ${userToSave.name}` });
        toast.success('User created successfully!');
      }
      setModalShow(false);
      fetchUsers();
    } catch (error) {
      toast.error("Failed to save user.");
      console.error(error);
    }
  };

  const handleDelete = async (userToDelete) => {
    if (userToDelete.id === loggedInUser.id) {
        toast.error("You cannot delete your own account.");
        return;
    }
    if (window.confirm(`Are you sure you want to delete user: ${userToDelete.name}?`)) {
        await dbService.delete('users', userToDelete.id);
        await dbService.addLog({ user: loggedInUser.name, action: 'Deleted User', details: `User: ${userToDelete.name}` });
        toast.warn('User deleted.');
        fetchUsers();
    }
  };

  return (
    <PageContainer title="User Administration">
      <div className="d-flex justify-content-between align-items-center mb-3">
        {permissions.canCreateUsers ? (
          <Button variant="primary" onClick={() => { setCurrentUser(null); setModalShow(true); }}>
            <PersonPlusFill className="me-2" /> Add New User
          </Button>
        ) : (
          <OverlayTrigger overlay={<Tooltip>Only Admins can create new users.</Tooltip>}>
            <span className="d-inline-block">
                <Button variant="primary" disabled style={{ pointerEvents: 'none' }}>
                    <PersonPlusFill className="me-2" /> Add New User
                </Button>
            </span>
          </OverlayTrigger>
        )}
      </div>
      <div className="card">
        <div className="card-body">
            <div className="table-responsive">
                <table className="table table-hover align-middle">
                <thead>
                  <tr>
                    <th>Name</th><th>Email</th><th>Role</th><th>Team/Group</th><th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                {users.map(user => (
                    <tr key={user.id}>
                      <td>{user.name}</td><td>{user.email}</td><td>{user.role}</td><td>{user.team}</td>
                      <td><span className={`badge bg-${user.status === 'Active' ? 'success' : 'secondary'}`}>{user.status}</span></td>
                      <td>
                        {permissions.canEditUsers ? (
                            <Button variant="outline-secondary" size="sm" className="me-2" title="Edit User" onClick={() => { setCurrentUser(user); setModalShow(true); }}>
                                <PencilSquare />
                            </Button>
                        ) : <DisabledButton className="me-2" tooltipText="Permission denied"><PencilSquare /></DisabledButton>}
                        
                        {permissions.canDeleteUsers ? (
                            <Button variant="outline-danger" size="sm" title="Delete User" onClick={() => handleDelete(user)}>
                                <Trash />
                            </Button>
                        ) : <DisabledButton tooltipText="Permission denied"><Trash /></DisabledButton>}
                      </td>
                    </tr>
                ))}
                </tbody>
            </table>
          </div>
        </div>
      </div>
       {modalShow && (
        <UserModal show={modalShow} onHide={() => setModalShow(false)} onSave={handleSave} user={currentUser} />
      )}
    </PageContainer>
  );
};

export default UserList;